import {AfterViewInit, Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {AuthService} from "@app/core/services/auth.service";
import {HTTP_STATUS_CODE} from "@app/core/constants/system.constants";
import {OtpService} from "@app/core/services/otp.service";
import {environment} from "@env/environment";
import {Location} from "@angular/common";

declare var window: any;
declare var grecaptcha: any;

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit, AfterViewInit {
  bgLoginPath = environment.bgLogin;
  form: FormGroup = this.fb.group({
    loginName: [null, Validators.required],
    otpCode: [null, Validators.required],
    newPassword: [null, Validators.required],
    retypePassword: [null, Validators.required],
    captcha: [null, Validators.required],
    // 2FA — chỉ bắt buộc nếu tài khoản đã bật 2FA (BE tự kiểm tra)
    totpCode: [null]
  });
  isSubmitted = false;
  isFocus2 = false;
  isFocus3 = false;
  isLoading = false;
  captchaToken: string | null = null;

  isStep1 = true;
  show2fa = false; // chỉ hiện ô nhập mã 2FA khi BE báo tài khoản đã bật 2FA
  sentOtpEmail: string | null = null;
  passwordRules = {
    length: false,
    complexity: false,
    special: false
  };

  resendDisabled = false;
  resendCountdown = 0;
  private resendTimer: any;


  constructor(
    private authService: AuthService,
    private readonly router: Router,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private otpService: OtpService,
    private location: Location
  ) {
  }

  ngOnInit(): void {
    window.onCaptchaResolved = (token: string) => {
      this.captchaToken = token;
      this.form.get('captcha')?.setValue(token);
    };
    this.form.get('newPassword')?.valueChanges.subscribe(value => {
      this.checkPasswordRules(value || '');
    });
  }

  checkPasswordRules(password: string) {
    this.passwordRules.length = password.length >= 8;
    this.passwordRules.complexity =
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password);
    this.passwordRules.special =
      /[~!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (window.grecaptcha) {
        clearInterval(interval);
        this.renderCaptcha();
      }
    }, 300);
  }

  get f() {
    return this.form.controls;
  }

  onLabelClick2() {
    this.isFocus2 = !this.isFocus2;
  }

  onLabelClick3() {
    this.isFocus3 = !this.isFocus3;
  }

  back() {
    if (this.isStep1) {
      this.location.back();
    } else {
      this.isStep1 = true;

      this.form.get('captcha')?.reset();
      this.captchaToken = null;

      setTimeout(() => {
        this.renderCaptcha();
      });
    }
  }

  renderCaptcha() {
    if (!window.grecaptcha) return;
    const el = document.getElementById('recaptcha');
    if (!el) return;
    el.innerHTML = '';
    grecaptcha.render('recaptcha', {
      sitekey: environment.siteKeyCaptcha,
      callback: (token: string) => {
        this.captchaToken = token;
        this.form.get('captcha')?.setValue(token);
      }
    });
  }

  resend(): void {
    if (this.resendDisabled) {
      return;
    }
    this.resendDisabled = true;
    this.resendCountdown = 90;
    const data = {
      userName: this.f.loginName.value,
      captcha: this.f.captcha.value
    };
    this.otpService.resend(data).subscribe({
      next: (res) => {
        if (res?.code === HTTP_STATUS_CODE.SUCCESS) {
          this.sentOtpEmail = res.data.email;
          this.toastrService.success(
            this.translate.instant(
              'common.user.validate.sendMailSuccess',
              {email: res.data.email}
            )
          );
          this.startResendCountdown();
        } else {
          this.toastrService.error(res?.message);
          this.resetResend();
        }
      },
      error: (err) => {
        this.toastrService.error(
          err?.error?.message || this.translate.instant('common.error.system')
        );
        this.resetResend();
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  startResendCountdown(): void {
    this.resendTimer = setInterval(() => {
      this.resendCountdown--;

      if (this.resendCountdown <= 0) {
        this.resetResend();
      }
    }, 1000);
  }

  resetResend(): void {
    this.resendDisabled = false;
    this.resendCountdown = 0;

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  submitForm(): void {
    this.isSubmitted = true;
    if (this.form.valid) {
      if (this.f.newPassword.value !== this.f.retypePassword.value) {
        this.toastrService.error(this.translate.instant('common.user.validate.errorRetypePassword'));
        return;
      }
      if (this.validatePassword(this.f.newPassword.value) !== null) {
        this.toastrService.error(this.validatePassword(this.f.newPassword.value));
        return;
      }
      this.isLoading = true;
      const fromObject = this.form.value;
      this.authService.forgotPassword(fromObject).subscribe({
        next: (res) => {
          if (res?.code === HTTP_STATUS_CODE.SUCCESS) {
            this.toastrService.success(
              this.translate.instant('common.user.validate.success')
            );
            this.authService.logout();
          } else if (res?.code === 'MFA_REQUIRED') {
            this.show2fa = true;
            this.toastrService.info('Tài khoản có bật xác thực 2 lớp, vui lòng nhập mã 2FA.');
          } else {
            this.toastrService.error(res?.message);
          }
        },
        error: (err) => {
          this.toastrService.error(
            err?.error?.message || this.translate.instant('common.error.system')
          );
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    }
  }

  continue() {
    this.isSubmitted = true;
    if (!this.f.loginName.value || this.f.loginName.value.trim() === '') {
      this.toastrService.error(this.translate.instant('common.user.validate.emptyLoginName'));
      return;
    }
    if (!this.f.captcha.value || this.f.captcha.value.trim() === '') {
      this.toastrService.error(this.translate.instant('common.user.validate.emptyCaptcha'));
      return;
    }
    const data = {
      userName: this.f.loginName.value,
      captcha: this.f.captcha.value
    };
    this.isLoading = true;
    this.otpService.getOtpAndSendMail(data).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.code === HTTP_STATUS_CODE.SUCCESS) {
          this.sentOtpEmail = res.data.email;
          this.toastrService.success(
            this.translate.instant(
              'common.user.validate.sendMailSuccess',
              {email: res.data.email}
            )
          );
          this.isStep1 = false;
          this.isSubmitted = false;
        } else {
          this.toastrService.error(res?.message || this.translate.instant('common.error.system'));
          this.resetCaptcha();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastrService.error(
          err?.error?.message || this.translate.instant('common.error.system')
        );
        this.resetCaptcha();
      }
    });
  }

  private resetCaptcha(): void {
    this.form.get('captcha')?.reset();
    this.captchaToken = null;
    setTimeout(() => this.renderCaptcha());
  }

  validatePassword(password) {
    const specialCharacters = /[~!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
    const lowercase = /[a-z]/;
    const uppercase = /[A-Z]/;
    const digits = /[0-9]/;
    if (password.length < 8) {
      return this.translate.instant('common.user.validate.error');
    }
    if (!lowercase.test(password) || !uppercase.test(password) || !digits.test(password) || !specialCharacters.test(password)) {
      return this.translate.instant('common.user.validate.error');
    }
    return null;
  }
}
